import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../store/selectors';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import createNotification from '../../../utils/createNotification';
import {
    PaymentDialog,
    PriceItem,
} from '../../../components/PaymentDialog/PaymentDialog';
import {
    InvoiceItemReferenceType,
    InvoiceItemReferenceTypeOptions,
} from '../../../models/Invoice/Enums/InvoiceItemReferenceTypeEnum';
import { FailedEngagementFeePaymentDetails } from './FailedEngagementFeePaymentDetails';
import { InvoiceType } from '../../../models/Invoice/Enums/InvoiceTypeEnum';

interface PaymentPopupProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    paymentSuccess: () => void;
    engagementId: number;
    retryPaymentDetails: FailedEngagementFeePaymentDetails;
    fteDvms: number;
    activeBuyers: number;
    isWalletCreditsForMonthlyFeesAllowed: boolean;
}

export const PaymentPopup = (props: PaymentPopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const stripe = useStripe();
    const elements = useElements();
    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [storeCreditsBalance, setStoreCreditsBalance] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);
    const paymentDetails = props.retryPaymentDetails;

    useEffect(() => {
        setOriginalServicePrice(paymentDetails.amount);
    }, []);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    // #1410: only the store-credit share of applied credits is non-creditable (flex and
    // reward credits keep earning credit). The wallet spends Store -> Flex -> Reward, so
    // that share is min(applied, store balance) — same math the backend stamps.
    const storeCreditsApplied = Math.min(appliedCredits, storeCreditsBalance);
    const creditablePortionAfterCredits =
        paymentDetails.amount > 0
            ? (paymentDetails.creditablePortion *
                  Math.max(0, paymentDetails.amount - storeCreditsApplied)) /
              paymentDetails.amount
            : paymentDetails.creditablePortion;

    const getMonthlyRetainerPriceList = (): PriceItem[] => [
        {
            priceType: 'summary',
            label: `${
                InvoiceItemReferenceTypeOptions.find(
                    (option) =>
                        option.value === paymentDetails.invoiceItemReferenceType
                )?.label
            }`,
        },
        {
            priceType: 'regular',
            label: 'Month Charged:',
            value: paymentDetails.itemsCount,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: paymentDetails.amount,
            valueType: 'currency',
        },
        {
            priceType: 'regular',
            label: 'Creditable Portion:',
            value: creditablePortionAfterCredits,
            valueType: 'currency',
        },
    ];

    const getLateFeePriceList = (): PriceItem[] => [
        {
            priceType: 'summary',
            label: `Late Fee`,
        },
        {
            priceType: 'regular',
            label: 'Late Days:',
            value: paymentDetails.itemsCount,
            valueType: 'number',
        },
        {
            priceType: 'regular',
            label: 'Seller FTE DVMs:',
            value: props.fteDvms,
            valueType: 'number',
        },
        {
            priceType: 'regular',
            label: 'Active Buyers:',
            value: props.activeBuyers,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: paymentDetails.amount,
            valueType: 'currency',
        },
    ];

    const getPriceListByInvoiceType = () => {
        switch (paymentDetails.invoiceType) {
            case InvoiceType.EngagementMonthlyRetainerFee:
                return getMonthlyRetainerPriceList();
            case InvoiceType.EngagementLateFee:
                return getLateFeePriceList();
            default:
                return [];
        }
    };

    // The monthly engagement fee must not be paid with wallet credits when an
    // admin has switched the option off for this engagement (see story 1359 /
    // hotfix 1365). Late fees are outside that toggle and keep credits.
    const hideWalletCredits =
        paymentDetails.invoiceType ===
            InvoiceType.EngagementMonthlyRetainerFee &&
        !props.isWalletCreditsForMonthlyFeesAllowed;

    const directPayment = () => payFailedFee(false);

    const payFailedFee = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        axios
            .post('/api/marketplace/MarketplacePayments/PayFailedFee', {
                walletId: 0,
                engagementId: props.engagementId,
                useWalletBalance: !!appliedCredits,
                walletAmount: appliedCredits,
                useSavedPaymentMethod: shouldUseSavedPaymentMethod,
                source: paymentTokenId,
                totalPrice: originalServicePrice,
                invoiceId: paymentDetails.invoiceId,
                paymentPackageIds: paymentDetails.paymentPackageIds,
                invoiceItemIds: paymentDetails.invoiceItemIds,
                invoiceItemReferenceType:
                    paymentDetails.invoiceItemReferenceType,
                invoiceType: paymentDetails.invoiceType,
            })
            .then((response: any) => {
                if (response?.response?.status == 402) {
                    createNotification(
                        response.response.data?.title ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                    setIsPaymentInProcess(false);
                    props.setIsVisible(false);
                } else {
                    handlePricingUpdatedError(response);
                    setTimeout(() => {
                        setIsPaymentInProcess(false);
                        props.paymentSuccess();
                    }, 3000);
                }
            })
            .catch((err: any) => console.log(err));
    };

    const handlePricingUpdatedError = (response: any) => {
        if (response.response?.status === 422) {
            createNotification(
                `${
                    InvoiceItemReferenceTypeOptions.find(
                        (option) =>
                            option.value ===
                            paymentDetails.invoiceItemReferenceType
                    )?.label
                } has been changed, please try again`,
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Price changed');
        }

        if (response.response?.status === 500) {
            createNotification(
                response.response.data?.title ??
                    'An unexpected error occured. Please try again later',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Bad request');
        }
    };

    return (
        <div className="payment-popup-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={getPriceListByInvoiceType()}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                onStoreCreditsLoaded={(value) => setStoreCreditsBalance(value)}
                chargeRequest={payFailedFee}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                hideWalletCredits={hideWalletCredits}
                isAuthorizationRequired={false}/>
        </div>
    );
};
